import React, { FC, useCallback, useRef } from 'react';
import Notifications from '../Notifications';
import classes from './styles/index.module.scss';
import { CopyToClipboardProps } from './types';
import { translationCodes } from '../../data/translations/translationModuleCodes';
import { useTranslationAPI } from '../../modules/translations';

const CopyToClipboard: FC<React.PropsWithChildren<CopyToClipboardProps>> = ({
	value,
	render,
	handleCopyEvent,
	afterCopySuccessMessage,
	afterCopyErrorMessage,
	stopPropagation = true,
}) => {
	const { translate } = useTranslationAPI();
	const textarea = useRef<HTMLTextAreaElement>(null);
	const defaultSuccessMessage = translate(translationCodes.copiedToClipBoard);

	const copyEventListener = useCallback(
		(e) => {
			e.preventDefault();

			if (typeof handleCopyEvent !== 'undefined') {
				return handleCopyEvent(e);
			}

			const value = e.target.value;
			e.clipboardData.setData('text/plain', value);
		},
		[handleCopyEvent],
	);

	const handleCopyButtonClick = useCallback(
		(e) => {
			if (stopPropagation) {
				e.stopPropagation();
			}

			if (textarea.current === null) {
				return;
			}

			textarea.current.select();
			document.addEventListener('copy', copyEventListener);
			const isCopySuccess = document.execCommand('copy');
			document.removeEventListener('copy', copyEventListener);

			if (isCopySuccess) {
				Notifications.success(afterCopySuccessMessage || defaultSuccessMessage);
			}

			if (!isCopySuccess && typeof afterCopyErrorMessage !== 'undefined') {
				Notifications.success(afterCopyErrorMessage);
			}
		},
		[stopPropagation, copyEventListener, afterCopyErrorMessage, afterCopySuccessMessage, defaultSuccessMessage],
	);

	return (
		<>
			<textarea
				className={classes.copyToClipboardTextarea}
				ref={textarea}
				defaultValue={value}
				readOnly
			/>
			{render({
				onCopyButtonClick: handleCopyButtonClick,
			})}
		</>
	);
};

export { CopyToClipboard };
